/**
 * 编辑项目对话框（参考电商复刻视觉语言）
 *
 * 可改字段：
 *   - 项目名 / 简介
 *   - 图片模型
 *   - 视频模型（带兼容性预检：aspect/resolution 不兼容 → 拒绝；
 *               有 shot 不兼容 → 黄色警告，让用户知情后再确认）
 *
 * 不允许在此处修改：画面比例、清晰度（创建后定型，避免破坏已有素材）。
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, X, Pencil, AlertTriangle, CheckCircle2 } from 'lucide-react';
import toast from '../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
  type DirectorProject,
  type DirectorCompatibleVideoModel,
} from '../services/directorApi';
import { api as imageApi, type ModelConfig as ImageModelConfig } from '../../../services/api';

interface Props {
  open: boolean;
  project:
    | Pick<
        DirectorProject,
        'id' | 'name' | 'description' | 'imageModelId' | 'videoModelId' | 'aspectRatio' | 'resolution'
      >
    | null;
  onClose: () => void;
  onSaved: () => void;
}

type PreviewResult = Awaited<ReturnType<typeof directorApi.previewVideoModelChange>>;

export function EditProjectDialog({ open, project, onClose, onSaved }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageModelId, setImageModelId] = useState('');
  const [videoModelId, setVideoModelId] = useState('');

  const [imageModels, setImageModels] = useState<ImageModelConfig[]>([]);
  const [videoModels, setVideoModels] = useState<DirectorCompatibleVideoModel[]>([]);
  const [loadingImageModels, setLoadingImageModels] = useState(false);
  const [loadingVideoModels, setLoadingVideoModels] = useState(false);

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 重置 / 初始化
  useEffect(() => {
    if (open && project) {
      setName(project.name);
      setDescription(project.description ?? '');
      setImageModelId(project.imageModelId ?? '');
      setVideoModelId(project.videoModelId ?? '');
      setPreview(null);
    }
  }, [open, project]);

  // 拉图片模型
  useEffect(() => {
    if (!open) return;
    setLoadingImageModels(true);
    imageApi
      .getModels()
      .then((d) => setImageModels(d.data))
      .catch((err) => {
        console.error(err);
        toast.error('图片模型加载失败');
      })
      .finally(() => setLoadingImageModels(false));
  }, [open]);

  // 拉视频模型（仅导演模式兼容）
  useEffect(() => {
    if (!open) return;
    setLoadingVideoModels(true);
    directorApi
      .listCompatibleVideoModels()
      .then((list) => setVideoModels(list))
      .catch((err) => {
        console.error(err);
        toast.error('视频模型加载失败');
      })
      .finally(() => setLoadingVideoModels(false));
  }, [open]);

  const videoModelChanged = useMemo(
    () => !!project && videoModelId && videoModelId !== project.videoModelId,
    [videoModelId, project]
  );

  // 视频模型改动时触发 dry-run 预检
  useEffect(() => {
    if (!project || !videoModelId) {
      setPreview(null);
      return;
    }
    if (videoModelId === project.videoModelId) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    setPreview(null);
    directorApi
      .previewVideoModelChange(project.id, videoModelId)
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof DirectorApiError ? err.message : '兼容性预检失败');
          setPreview(null);
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [videoModelId, project]);

  if (!open || !project) return null;

  // 切换视频模型：aspect/resolution 不兼容时禁用提交
  const blockingIncompatibility =
    videoModelChanged &&
    !!preview &&
    (!preview.aspectCompatible || !preview.resolutionCompatible);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('项目名不能为空');
      return;
    }
    if (blockingIncompatibility) {
      toast.error('所选视频模型不支持本项目当前比例 / 清晰度');
      return;
    }
    setSubmitting(true);
    try {
      await directorApi.updateProject(project.id, {
        name: name.trim(),
        description: description.trim() || null,
        imageModelId: imageModelId || null,
        videoModelId: videoModelChanged ? videoModelId : undefined,
      });
      toast.success('已保存');
      onSaved();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30">
        {/* 顶部 */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-sky-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Pencil className="h-3 w-3 text-sky-200" />
                编辑项目
              </div>
              <h2 className="text-lg font-semibold leading-tight">修改项目设置</h2>
              <p className="mt-1.5 text-xs leading-5 text-slate-300">
                画面比例和清晰度创建后不可改；视频模型可切换，已生成的视频候选不会受影响
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
              aria-label="关闭"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 表单 */}
        <div className="flex-1 space-y-5 overflow-y-auto bg-slate-50 p-5 sm:p-6">
          {/* 项目名 */}
          <div>
            <label className="mb-1.5 flex items-center gap-1 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
              项目名 <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 60))}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            />
            <p className="mt-1 text-right text-[11px] text-slate-400">{name.length}/60</p>
          </div>

          {/* 简介 */}
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
              简介（可选）
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 200))}
              rows={3}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            />
            <p className="mt-1 text-right text-[11px] text-slate-400">{description.length}/200</p>
          </div>

          {/* 当前比例 + 清晰度（只读展示） */}
          <div className="rounded-2xl bg-slate-100 px-4 py-3 text-xs leading-5 text-slate-600">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-700">画面比例</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-slate-700">
                {project.aspectRatio}
              </span>
              <span className="ml-2 font-semibold text-slate-700">清晰度</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-slate-700">
                {project.resolution}
              </span>
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">
              已锁定，不可修改。如需改用其他比例 / 清晰度，建议复制此项目后调整。
            </p>
          </div>

          {/* 视频模型 */}
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
              视频模型
            </label>
            {loadingVideoModels ? (
              <div className="rounded-2xl bg-white px-4 py-2.5 text-sm text-slate-500">
                加载中...
              </div>
            ) : videoModels.length === 0 ? (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">
                暂无可用视频模型
              </div>
            ) : (
              <select
                value={videoModelId}
                onChange={(e) => setVideoModelId(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-300"
              >
                {videoModels.map((m) => (
                  <option key={m.modelId} value={m.modelId}>
                    {m.name}
                  </option>
                ))}
                {/* 当前模型若不在兼容列表里（已停用 / 不再支持 img2video），仍允许保留显示 */}
                {!videoModels.some((m) => m.modelId === project.videoModelId) && (
                  <option value={project.videoModelId}>
                    {project.videoModelId}（当前 · 不在兼容列表）
                  </option>
                )}
              </select>
            )}
            <p className="mt-1.5 text-[11px] leading-4 text-slate-500">
              切换模型不会影响已生成的视频候选；后续激活会使用新模型。
            </p>

            {/* 预检结果 */}
            {videoModelChanged && (
              <div className="mt-3">
                {previewing ? (
                  <div className="flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    正在检查兼容性...
                  </div>
                ) : preview ? (
                  <PreviewSummary preview={preview} />
                ) : null}
              </div>
            )}
          </div>

          {/* 图片模型 */}
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
              图片模型
            </label>
            {loadingImageModels ? (
              <div className="rounded-2xl bg-white px-4 py-2.5 text-sm text-slate-500">
                加载中...
              </div>
            ) : imageModels.length === 0 ? (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">
                暂无可用图片模型
              </div>
            ) : (
              <select
                value={imageModelId}
                onChange={(e) => setImageModelId(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-300"
              >
                <option value="">使用系统默认</option>
                {imageModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            )}
            <p className="mt-1.5 text-[11px] leading-4 text-slate-500">
              用于生成分镜关键帧、角色定妆照、场景设定图。
            </p>
          </div>
        </div>

        {/* 底部 */}
        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !name.trim() || blockingIncompatibility || previewing}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            保存
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- 子组件：兼容性预检结果展示 ----------------

function PreviewSummary({ preview }: { preview: PreviewResult }) {
  const aspectOk = preview.aspectCompatible;
  const resolutionOk = preview.resolutionCompatible;

  // 红色硬阻塞：aspect 或 resolution 不兼容
  if (!aspectOk || !resolutionOk) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-xs leading-5 text-rose-700">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">无法切换到此模型</div>
            <ul className="mt-1.5 space-y-0.5 text-rose-600">
              {!aspectOk && (
                <li>
                  · 该模型不支持当前画面比例（支持：
                  {preview.newSupportedAspectRatios.join(', ')}）
                </li>
              )}
              {!resolutionOk && (
                <li>
                  · 该模型不支持当前清晰度（支持：
                  {preview.newSupportedResolutions.join(', ')}）
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // 黄色警告：仅有部分 shot 不兼容
  if (preview.incompatibleShots.length > 0) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">
              {preview.incompatibleShots.length} 个分镜将无法用新模型激活
            </div>
            <p className="mt-0.5 text-[11px] text-amber-700">
              新模型支持时长 {preview.newDurationLabel}
              {preview.newMaxReferenceImageCount > 0
                ? ` · 多参考图上限 ${preview.newMaxReferenceImageCount} 张`
                : ''}
            </p>
            <ul className="mt-1.5 max-h-32 space-y-1 overflow-y-auto pr-1">
              {preview.incompatibleShots.map((s) => (
                <li key={s.shotId} className="rounded-lg bg-white/60 px-2 py-1">
                  <span className="font-semibold">
                    分镜 #{s.shotOrderInProject + 1}
                    {s.title ? ` · ${s.title}` : ''}
                  </span>
                  <ul className="ml-3 mt-0.5 list-disc text-amber-700">
                    {s.reasons.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11px] text-amber-700">
              不阻塞保存。这些分镜在调整时长 / 帧数后即可重新激活；已生成的视频不受影响。
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 绿色：完全兼容
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-xs leading-5 text-emerald-700">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">所有分镜均可用新模型激活</div>
          <p className="mt-0.5 text-[11px] text-emerald-700">
            新模型 {preview.newModelName}
            <span className="mx-1">·</span>
            支持时长 {preview.newDurationLabel}
            {preview.newMaxReferenceImageCount > 0
              ? ` · 多参考图上限 ${preview.newMaxReferenceImageCount} 张`
              : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
